import { h } from "preact";
import { useRef } from "preact/hooks";
import { createPortal } from "preact/compat";
import { CSSTransition } from "react-transition-group";
import { CaretLeft, Close } from "../../helpers/icons";
import MonogramContent from "./monogram-content";

const SCROLL_LOCK_KEY = "monogram-modal";

/**
 * Full-screen monogram personalization modal. The content (preview, steps, and
 * footer) lives in <MonogramContent />.
 */
export default function Monogram({
  open,
  onClose,
  onAdd,
  onClosed,
  returnFocusRef,
  monogram,
  initialSelection,
  currentVariant,
}) {
  const strings = window.localeStrings?.monogram || {};
  const productTitle = currentVariant?.product?.title;

  const modalRef = useRef();
  const closeRef = useRef();

  const onEnter = () => {
    window.lockScroll(SCROLL_LOCK_KEY);
    if (modalRef.current) {
      window.trapFocus(modalRef.current, closeRef.current);
    }
  };

  // `onClosed` lets a host that mounted the modal on demand tear itself down
  // once the exit transition has finished.
  const onExited = () => {
    window.unlockScroll(SCROLL_LOCK_KEY);
    window.removeTrapFocus(returnFocusRef?.current, modalRef.current);
    onClosed?.();
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") onClose();
  };

  return createPortal(
    <CSSTransition
      mountOnEnter={true}
      unmountOnExit={true}
      in={open}
      nodeRef={modalRef}
      classNames="monogram-modal"
      timeout={450}
      onEnter={onEnter}
      onExited={onExited}
    >
      <div
        className="monogram-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={strings.monogram}
        onKeyDown={onKeyDown}
      >
        <header className="monogram-modal__header">
          <button
            type="button"
            className="monogram-modal__back"
            onClick={onClose}
          >
            <CaretLeft />
            <span className="subheading-1">{strings.back_to_product}</span>
          </button>

          {productTitle && (
            <h2 className="monogram-modal__title h5">{productTitle}</h2>
          )}

          <button
            type="button"
            className="monogram-modal__close"
            aria-label={strings.close}
            onClick={onClose}
            ref={closeRef}
          >
            <Close />
          </button>
        </header>

        <MonogramContent
          onClose={onClose}
          onAdd={onAdd}
          monogram={monogram}
          initialSelection={initialSelection}
          currentVariant={currentVariant}
        />
      </div>
    </CSSTransition>,
    document.body,
  );
}
